'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { ArrowRight, Plus, Search, Filter, ChevronDown, ChevronUp, CheckCircle, Clock, Truck, XCircle, FileText, Edit3, Trash2, Eye, X, Package, ChevronLeft, ChevronRight as ChevronRightIcon,  } from 'lucide-react';
import { MOCK_TRANSFERS, EggTransfer } from '../../components/eggProductionData';
import Icon from '@/components/ui/AppIcon';
import { useRole } from '@/lib/useRole';


// ─── Types ────────────────────────────────────────────────────────────────────

interface TransferFormData {
  date: string;
  sourceFarm: string;
  sourceStore: string;
  destinationFarm: string;
  destinationStore: string;
  eggGrade: string;
  quantity: number | '';
  trays: number | '';
  looseEggs: number | '';
  transferredBy: string;
  notes: string;
}

interface FormErrors {
  [key: string]: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const FARMS = ['Idudi Poultry Farm', 'Kampala Demo Farm', 'Jinja Branch Farm'];
const STORES = ['Main Egg Store', 'Cold Store A', 'Dispatch Area', 'Grading Room', 'Hatchery Store'];
const EGG_GRADES = ['Grade A Large', 'Grade A Medium', 'Grade A Small', 'Grade B', 'Hatching Grade', 'Rejected'];
const STAFF = ['James Okello', 'Sarah Namukasa', 'Ogenga Paul', 'Mary Akello', 'David Mugisha'];
const TRAY_SIZE = 30;

const STATUS_CONFIG: Record<EggTransfer['status'], { label: string; color: string; icon: React.ElementType }> = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-600', icon: FileText },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  in_transit: { label: 'In Transit', color: 'bg-blue-100 text-blue-700', icon: Truck },
  received: { label: 'Received', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-600', icon: XCircle },
};

const WORKFLOW_STEPS: EggTransfer['status'][] = ['draft', 'pending', 'in_transit', 'received'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function generateTransferNumber(): string {
  const num = Math.floor(Math.random() * 900) + 100;
  return `ET-2026-0${num}`;
}

function calcTraysFromQty(qty: number): { trays: number; loose: number } {
  return { trays: Math.floor(qty / TRAY_SIZE), loose: qty % TRAY_SIZE };
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: EggTransfer['status'] }) {
  const cfg = STATUS_CONFIG[status];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color}`}>
      <Icon size={11} />
      {cfg.label}
    </span>
  );
}

// ─── Workflow Stepper ─────────────────────────────────────────────────────────

function WorkflowStepper({ status }: { status: EggTransfer['status'] }) {
  const currentIdx = WORKFLOW_STEPS.indexOf(status);
  const isCancelled = status === 'cancelled';

  return (
    <div className="flex items-center gap-0">
      {WORKFLOW_STEPS.map((step, idx) => {
        const cfg = STATUS_CONFIG[step];
        const Icon = cfg.icon;
        const isCompleted = !isCancelled && idx < currentIdx;
        const isActive = !isCancelled && idx === currentIdx;
        const isPending = isCancelled || idx > currentIdx;

        return (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                  isCompleted
                    ? 'bg-green-500 border-green-500 text-white'
                    : isActive
                    ? 'bg-green-600 border-green-600 text-white shadow-md shadow-green-200'
                    : isCancelled
                    ? 'bg-red-100 border-red-300 text-red-400' :'bg-gray-100 border-gray-200 text-gray-400'
                }`}
              >
                {isCompleted ? <CheckCircle size={14} /> : <Icon size={13} />}
              </div>
              <span
                className={`text-xs mt-1 font-medium whitespace-nowrap ${
                  isActive ? 'text-green-700' : isPending ? 'text-gray-400' : 'text-green-600'
                }`}
              >
                {cfg.label}
              </span>
            </div>
            {idx < WORKFLOW_STEPS.length - 1 && (
              <div
                className={`h-0.5 w-8 sm:w-12 mb-4 mx-1 transition-all ${
                  isCompleted ? 'bg-green-400' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
      {isCancelled && (
        <div className="ml-3 flex items-center gap-1 text-xs text-red-500 font-semibold mb-4">
          <XCircle size={13} /> Cancelled
        </div>
      )}
    </div>
  );
}

// ─── Transfer Form Modal ──────────────────────────────────────────────────────

function TransferFormModal({
  onClose,
  onSave,
  editTransfer,
}: {
  onClose: () => void;
  onSave: (data: EggTransfer) => void;
  editTransfer?: EggTransfer | null;
}) {
  const today = new Date().toISOString().split('T')[0];

  const [form, setForm] = useState<TransferFormData>({
    date: editTransfer?.date ?? today,
    sourceFarm: editTransfer?.sourceFarm ?? 'Idudi Poultry Farm',
    sourceStore: editTransfer?.sourceStore ?? 'Main Egg Store',
    destinationFarm: editTransfer?.destinationFarm ?? '',
    destinationStore: editTransfer?.destinationStore ?? '',
    eggGrade: editTransfer?.eggGrade ?? '',
    quantity: editTransfer?.quantity ?? '',
    trays: editTransfer?.trays ?? '',
    looseEggs: editTransfer?.looseEggs ?? '',
    transferredBy: editTransfer?.transferredBy ?? '',
    notes: editTransfer?.notes ?? '',
  });

  const [saveAsDraft, setSaveAsDraft] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Auto-calculate trays when quantity changes
  function handleQtyChange(val: string) {
    const qty = parseInt(val) || 0;
    const { trays, loose } = calcTraysFromQty(qty);
    setForm(f => ({ ...f, quantity: qty || '', trays, looseEggs: loose }));
  }

  function validate(): boolean {
    const errs: FormErrors = {};
    if (!form.date) errs.date = 'Date is required';
    if (!form.sourceFarm) errs.sourceFarm = 'Source farm is required';
    if (!form.sourceStore) errs.sourceStore = 'Source store is required';
    if (!form.destinationFarm) errs.destinationFarm = 'Destination farm is required';
    if (!form.destinationStore) errs.destinationStore = 'Destination store is required';
    if (!form.eggGrade) errs.eggGrade = 'Egg grade is required';
    if (!form.quantity || Number(form.quantity) <= 0) errs.quantity = 'Quantity must be greater than 0';
    if (!form.transferredBy) errs.transferredBy = 'Transferred by is required';
    if (form.sourceFarm === form.destinationFarm && form.sourceStore === form.destinationStore) {
      errs.destinationStore = 'Destination must differ from source';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit(draft = false) {
    if (!validate()) return;
    const transfer: EggTransfer = {
      id: editTransfer?.id ?? `tr-${Date.now()}`,
      transferNumber: editTransfer?.transferNumber ?? generateTransferNumber(),
      date: form.date,
      sourceFarm: form.sourceFarm,
      sourceStore: form.sourceStore,
      destinationFarm: form.destinationFarm,
      destinationStore: form.destinationStore,
      eggGrade: form.eggGrade,
      quantity: Number(form.quantity),
      trays: Number(form.trays),
      looseEggs: Number(form.looseEggs),
      transferredBy: form.transferredBy,
      status: draft ? 'draft' : 'pending',
      notes: form.notes || undefined,
      createdAt: editTransfer?.createdAt ?? new Date().toISOString(),
    };
    onSave(transfer);
  }

  const inputCls = (field: string) =>
    `w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {editTransfer ? 'Edit Transfer' : 'New Egg Transfer'}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {editTransfer ? `Editing ${editTransfer.transferNumber}` : 'Create a new egg transfer between stores or farms'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Section: Transfer Details */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-green-100 text-green-700 text-xs flex items-center justify-center font-bold">1</span>
              Transfer Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={form.date}
                  onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  className={inputCls('date')}
                />
                {errors.date && <p className="text-xs text-red-500 mt-1">{errors.date}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Egg Grade <span className="text-red-500">*</span></label>
                <select
                  value={form.eggGrade}
                  onChange={e => setForm(f => ({ ...f, eggGrade: e.target.value }))}
                  className={inputCls('eggGrade')}
                >
                  <option value="">Select grade…</option>
                  {EGG_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
                {errors.eggGrade && <p className="text-xs text-red-500 mt-1">{errors.eggGrade}</p>}
              </div>
            </div>
          </div>

          {/* Section: Source */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-green-100 text-green-700 text-xs flex items-center justify-center font-bold">2</span>
              Source
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Source Farm <span className="text-red-500">*</span></label>
                <select
                  value={form.sourceFarm}
                  onChange={e => setForm(f => ({ ...f, sourceFarm: e.target.value }))}
                  className={inputCls('sourceFarm')}
                >
                  <option value="">Select farm…</option>
                  {FARMS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                {errors.sourceFarm && <p className="text-xs text-red-500 mt-1">{errors.sourceFarm}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Source Store <span className="text-red-500">*</span></label>
                <select
                  value={form.sourceStore}
                  onChange={e => setForm(f => ({ ...f, sourceStore: e.target.value }))}
                  className={inputCls('sourceStore')}
                >
                  <option value="">Select store…</option>
                  {STORES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {errors.sourceStore && <p className="text-xs text-red-500 mt-1">{errors.sourceStore}</p>}
              </div>
            </div>
          </div>

          {/* Section: Destination */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-bold">3</span>
              Destination
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Destination Farm <span className="text-red-500">*</span></label>
                <select
                  value={form.destinationFarm}
                  onChange={e => setForm(f => ({ ...f, destinationFarm: e.target.value }))}
                  className={inputCls('destinationFarm')}
                >
                  <option value="">Select farm…</option>
                  {FARMS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                {errors.destinationFarm && <p className="text-xs text-red-500 mt-1">{errors.destinationFarm}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Destination Store <span className="text-red-500">*</span></label>
                <select
                  value={form.destinationStore}
                  onChange={e => setForm(f => ({ ...f, destinationStore: e.target.value }))}
                  className={inputCls('destinationStore')}
                >
                  <option value="">Select store…</option>
                  {STORES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {errors.destinationStore && <p className="text-xs text-red-500 mt-1">{errors.destinationStore}</p>}
              </div>
            </div>
          </div>

          {/* Section: Quantity */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-green-100 text-green-700 text-xs flex items-center justify-center font-bold">4</span>
              Quantity
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Total Eggs <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  min="1"
                  placeholder="e.g. 900"
                  value={form.quantity}
                  onChange={e => handleQtyChange(e.target.value)}
                  className={inputCls('quantity')}
                />
                {errors.quantity && <p className="text-xs text-red-500 mt-1">{errors.quantity}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Full Trays</label>
                <input
                  type="number"
                  min="0"
                  value={form.trays}
                  readOnly
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                />
                <p className="text-xs text-gray-400 mt-1">Auto-calculated (÷{TRAY_SIZE})</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Loose Eggs</label>
                <input
                  type="number"
                  min="0"
                  value={form.looseEggs}
                  readOnly
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                />
                <p className="text-xs text-gray-400 mt-1">Remainder after trays</p>
              </div>
            </div>
            {form.quantity && Number(form.quantity) > 0 && (
              <div className="mt-3 p-3 bg-green-50 border border-green-100 rounded-lg flex items-center gap-2">
                <Package size={14} className="text-green-600 flex-shrink-0" />
                <p className="text-xs text-green-700 font-medium">
                  {Number(form.quantity).toLocaleString()} eggs = {form.trays} full trays + {form.looseEggs} loose eggs
                </p>
              </div>
            )}
          </div>

          {/* Section: Staff & Notes */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-green-100 text-green-700 text-xs flex items-center justify-center font-bold">5</span>
              Staff & Notes
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Transferred By <span className="text-red-500">*</span></label>
                <select
                  value={form.transferredBy}
                  onChange={e => setForm(f => ({ ...f, transferredBy: e.target.value }))}
                  className={inputCls('transferredBy')}
                >
                  <option value="">Select staff…</option>
                  {STAFF.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {errors.transferredBy && <p className="text-xs text-red-500 mt-1">{errors.transferredBy}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
                <input
                  type="text"
                  placeholder="Optional transfer notes…"
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className={inputCls('notes')}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl gap-3 flex-wrap">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors">
            Cancel
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleSubmit(true)}
              className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
            >
              Save as Draft
            </button>
            <button
              onClick={() => handleSubmit(false)}
              className="px-5 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shadow-sm"
            >
              {editTransfer ? 'Update Transfer' : 'Create Transfer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Receive Confirmation Modal ───────────────────────────────────────────────

function ReceiveModal({
  transfer,
  onClose,
  onConfirm,
}: {
  transfer: EggTransfer;
  onClose: () => void;
  onConfirm: (receivedBy: string, notes: string) => void;
}) {
  const [receivedBy, setReceivedBy] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  function handleConfirm() {
    if (!receivedBy) { setError('Please select who received the transfer.'); return; }
    onConfirm(receivedBy, notes);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
              <CheckCircle size={20} className="text-green-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Confirm Receipt</h2>
              <p className="text-xs text-gray-500">{transfer.transferNumber}</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">From</span>
              <span className="font-semibold text-gray-800">{transfer.sourceFarm} — {transfer.sourceStore}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Grade</span>
              <span className="font-semibold text-gray-800">{transfer.eggGrade}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Quantity</span>
              <span className="font-semibold text-gray-800">{transfer.quantity.toLocaleString()} eggs ({transfer.trays} trays)</span>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Received By <span className="text-red-500">*</span></label>
            <select
              value={receivedBy}
              onChange={e => { setReceivedBy(e.target.value); setError(''); }}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${error ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
            >
              <option value="">Select staff member…</option>
              {STAFF.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Receipt Notes</label>
            <textarea
              rows={2}
              placeholder="Any notes on condition, count discrepancy, etc."
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-200 rounded-lg transition-colors">
            Cancel
          </button>
          <button onClick={handleConfirm} className="px-5 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shadow-sm">
            Confirm Receipt
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Transfer Detail Drawer ───────────────────────────────────────────────────

function TransferDetailDrawer({ transfer, onClose }: { transfer: EggTransfer; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-md bg-white shadow-2xl overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-900">{transfer.transferNumber}</h2>
            <p className="text-xs text-gray-500 mt-0.5">Transfer Details</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5 flex-1">
          {/* Status & Workflow */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Status Workflow</p>
            <WorkflowStepper status={transfer.status} />
          </div>

          {/* Details */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Transfer Information</p>
            {[
              { label: 'Date', value: transfer.date },
              { label: 'Egg Grade', value: transfer.eggGrade },
              { label: 'Quantity', value: `${transfer.quantity.toLocaleString()} eggs` },
              { label: 'Trays', value: `${transfer.trays} trays` },
              { label: 'Loose Eggs', value: `${transfer.looseEggs} eggs` },
            ].map(row => (
              <div key={row.label} className="flex justify-between text-sm">
                <span className="text-gray-500">{row.label}</span>
                <span className="font-semibold text-gray-800">{row.value}</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-green-50 border border-green-100 rounded-xl p-3">
              <p className="text-xs font-bold text-green-700 mb-2">Source</p>
              <p className="text-xs font-semibold text-gray-800">{transfer.sourceFarm}</p>
              <p className="text-xs text-gray-500">{transfer.sourceStore}</p>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
              <p className="text-xs font-bold text-blue-700 mb-2">Destination</p>
              <p className="text-xs font-semibold text-gray-800">{transfer.destinationFarm}</p>
              <p className="text-xs text-gray-500">{transfer.destinationStore}</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">People</p>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Transferred By</span>
              <span className="font-semibold text-gray-800">{transfer.transferredBy}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Received By</span>
              <span className="font-semibold text-gray-800">{transfer.receivedBy ?? <span className="text-gray-400 italic">Pending</span>}</span>
            </div>
          </div>

          {transfer.notes && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
              <p className="text-xs font-bold text-amber-700 mb-1">Notes</p>
              <p className="text-xs text-gray-700">{transfer.notes}</p>
            </div>
          )}

          <div className="text-xs text-gray-400">
            Created: {new Date(transfer.createdAt).toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Content ─────────────────────────────────────────────────────────────

export default function EggTransfersContent() {
  const [transfers, setTransfers] = useState<EggTransfer[]>([
    ...MOCK_TRANSFERS,
    {
      id: 'tr-003',
      transferNumber: 'ET-2026-010',
      date: '2026-09-01',
      sourceFarm: 'Idudi Poultry Farm',
      sourceStore: 'Grading Room',
      destinationFarm: 'Idudi Poultry Farm',
      destinationStore: 'Main Egg Store',
      eggGrade: 'Grade A Medium',
      quantity: 450,
      trays: 15,
      looseEggs: 0,
      transferredBy: 'Mary Akello',
      receivedBy: 'James Okello',
      status: 'received',
      notes: 'Post-grading transfer to main store.',
      createdAt: '2026-09-01T09:00:00Z',
    },
    {
      id: 'tr-004',
      transferNumber: 'ET-2026-009',
      date: '2026-08-30',
      sourceFarm: 'Idudi Poultry Farm',
      sourceStore: 'Hatchery Store',
      destinationFarm: 'Jinja Branch Farm',
      destinationStore: 'Main Egg Store',
      eggGrade: 'Hatching Grade',
      quantity: 300,
      trays: 10,
      looseEggs: 0,
      transferredBy: 'Ogenga Paul',
      status: 'pending',
      createdAt: '2026-08-30T08:00:00Z',
    },
    {
      id: 'tr-005',
      transferNumber: 'ET-2026-008',
      date: '2026-08-28',
      sourceFarm: 'Kampala Demo Farm',
      sourceStore: 'Main Egg Store',
      destinationFarm: 'Idudi Poultry Farm',
      destinationStore: 'Cold Store A',
      eggGrade: 'Grade B',
      quantity: 240,
      trays: 8,
      looseEggs: 0,
      transferredBy: 'David Mugisha',
      status: 'cancelled',
      notes: 'Cancelled — vehicle breakdown.',
      createdAt: '2026-08-28T10:00:00Z',
    },
  ]);

  const [showForm, setShowForm] = useState(false);
  const [editTransfer, setEditTransfer] = useState<EggTransfer | null>(null);
  const [viewTransfer, setViewTransfer] = useState<EggTransfer | null>(null);
  const [receiveTransfer, setReceiveTransfer] = useState<EggTransfer | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<EggTransfer['status'] | 'all'>('all');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [sortField, setSortField] = useState<'date' | 'quantity' | 'transferNumber'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 8;
  const { can } = useRole();
  const canManage = can('egg_transfers.manage');

  function showToast(msg: string, type: 'success' | 'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  function handleSave(data: EggTransfer) {
    setTransfers(prev => {
      const exists = prev.find(t => t.id === data.id);
      if (exists) return prev.map(t => t.id === data.id ? data : t);
      return [data, ...prev];
    });
    setShowForm(false);
    setEditTransfer(null);
    showToast(editTransfer ? `Transfer ${data.transferNumber} updated.` : `Transfer ${data.transferNumber} created.`);
  }

  function handleStatusChange(id: string, newStatus: EggTransfer['status']) {
    setTransfers(prev => prev.map(t => t.id === id ? { ...t, status: newStatus } : t));
    showToast(`Status updated to ${STATUS_CONFIG[newStatus].label}.`);
  }

  function handleReceive(receivedBy: string, notes: string) {
    if (!receiveTransfer) return;
    setTransfers(prev =>
      prev.map(t =>
        t.id === receiveTransfer.id
          ? { ...t, status: 'received', receivedBy, notes: notes || t.notes }
          : t
      )
    );
    setReceiveTransfer(null);
    showToast(`Transfer confirmed as received by ${receivedBy}.`);
  }

  function handleDelete(id: string) {
    const t = transfers.find(x => x.id === id);
    if (!t) return;
    if (!['draft', 'cancelled'].includes(t.status)) {
      showToast('Only Draft or Cancelled transfers can be deleted.', 'error');
      return;
    }
    setTransfers(prev => prev.filter(x => x.id !== id));
    showToast('Transfer deleted.');
  }

  function toggleSort(field: typeof sortField) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  }

  const filtered = useMemo(() => {
    let list = [...transfers];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        t.transferNumber.toLowerCase().includes(q) ||
        t.sourceFarm.toLowerCase().includes(q) ||
        t.destinationFarm.toLowerCase().includes(q) ||
        t.eggGrade.toLowerCase().includes(q) ||
        t.transferredBy.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') list = list.filter(t => t.status === statusFilter);
    if (gradeFilter !== 'all') list = list.filter(t => t.eggGrade === gradeFilter);
    list.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'date') cmp = a.date.localeCompare(b.date);
      else if (sortField === 'quantity') cmp = a.quantity - b.quantity;
      else cmp = a.transferNumber.localeCompare(b.transferNumber);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [transfers, search, statusFilter, gradeFilter, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // KPI summary
  const kpis = useMemo(() => ({
    total: transfers.length,
    inTransit: transfers.filter(t => t.status === 'in_transit').length,
    pending: transfers.filter(t => t.status === 'pending').length,
    received: transfers.filter(t => t.status === 'received').length,
    totalEggs: transfers.filter(t => t.status !== 'cancelled').reduce((s, t) => s + t.quantity, 0),
  }), [transfers]);

  function SortIcon({ field }: { field: typeof sortField }) {
    if (sortField !== field) return <ChevronDown size={12} className="text-gray-300" />;
    return sortDir === 'asc' ? <ChevronUp size={12} className="text-green-600" /> : <ChevronDown size={12} className="text-green-600" />;
  }

  return (
    <div className="p-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 text-white text-sm px-4 py-3 rounded-xl shadow-xl max-w-sm ${toast.type === 'error' ? 'bg-red-600' : 'bg-gray-900'}`}>
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${toast.type === 'error' ? 'bg-red-300' : 'bg-green-400'}`} />
          {toast.msg}
        </div>
      )}

      {/* Modals */}
      {(showForm || editTransfer) && (
        <TransferFormModal
          onClose={() => { setShowForm(false); setEditTransfer(null); }}
          onSave={handleSave}
          editTransfer={editTransfer}
        />
      )}
      {receiveTransfer && (
        <ReceiveModal
          transfer={receiveTransfer}
          onClose={() => setReceiveTransfer(null)}
          onConfirm={handleReceive}
        />
      )}
      {viewTransfer && (
        <TransferDetailDrawer transfer={viewTransfer} onClose={() => setViewTransfer(null)} />
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
            <Link href="/egg-production" className="hover:text-green-600 transition-colors">Egg Production</Link>
            <ChevronRightIcon size={12} />
            <span className="text-gray-600 font-medium">Transfers</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Egg Transfers</h1>
          <p className="text-sm text-gray-500 mt-1">Manage egg movements between stores, houses, and farms with full status tracking.</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className={`flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-sm ${!canManage ? 'hidden' : ''}`}
        >
          <Plus size={16} />
          New Transfer
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Total Transfers', value: kpis.total, icon: ArrowRight, bg: 'bg-gray-100', color: 'text-gray-600' },
          { label: 'Pending', value: kpis.pending, icon: Clock, bg: 'bg-amber-100', color: 'text-amber-600' },
          { label: 'In Transit', value: kpis.inTransit, icon: Truck, bg: 'bg-blue-100', color: 'text-blue-600' },
          { label: 'Received', value: kpis.received, icon: CheckCircle, bg: 'bg-green-100', color: 'text-green-600' },
          { label: 'Total Eggs Moved', value: kpis.totalEggs.toLocaleString(), icon: Package, bg: 'bg-green-100', color: 'text-green-600' },
        ].map(c => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-start gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${c.bg}`}>
                <Icon size={16} className={c.color} />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500 font-medium leading-tight">{c.label}</p>
                <p className="text-lg font-bold text-gray-900 mt-0.5">{c.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search transfers, farms, grades…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={14} className="text-gray-400" />
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value as EggTransfer['status'] | 'all'); setPage(1); }}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
          >
            <option value="all">All Statuses</option>
            {(Object.keys(STATUS_CONFIG) as EggTransfer['status'][]).map(s => (
              <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
            ))}
          </select>
          <select
            value={gradeFilter}
            onChange={e => { setGradeFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
          >
            <option value="all">All Grades</option>
            {EGG_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          {(search || statusFilter !== 'all' || gradeFilter !== 'all') && (
            <button
              onClick={() => { setSearch(''); setStatusFilter('all'); setGradeFilter('all'); setPage(1); }}
              className="flex items-center gap-1 px-3 py-2 text-xs font-semibold text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>
        <p className="text-xs text-gray-400 ml-auto">{filtered.length} transfer{filtered.length !== 1 ? 's' : ''}</p>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {paginated.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
              <ArrowRight size={24} className="text-gray-400" />
            </div>
            <p className="text-base font-bold text-gray-700 mb-1">No transfers found</p>
            <p className="text-sm text-gray-400 mb-4">
              {search || statusFilter !== 'all' || gradeFilter !== 'all' ?'Try adjusting your filters.' :'Create your first egg transfer to get started.'}
            </p>
            {!search && statusFilter === 'all' && gradeFilter === 'all' && canManage && (
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors"
              >
                <Plus size={15} /> New Transfer
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th
                      className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => toggleSort('transferNumber')}
                    >
                      <span className="flex items-center gap-1">Transfer # <SortIcon field="transferNumber" /></span>
                    </th>
                    <th
                      className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => toggleSort('date')}
                    >
                      <span className="flex items-center gap-1">Date <SortIcon field="date" /></span>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Source</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Destination</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Grade</th>
                    <th
                      className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => toggleSort('quantity')}
                    >
                      <span className="flex items-center gap-1">Qty / Trays <SortIcon field="quantity" /></span>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Transferred By</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Received By</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((t, i) => (
                    <tr key={t.id} className={`border-b border-gray-50 hover:bg-gray-50/70 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setViewTransfer(t)}
                          className="text-xs font-bold text-green-700 hover:text-green-800 hover:underline"
                        >
                          {t.transferNumber}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{t.date}</td>
                      <td className="px-4 py-3">
                        <p className="text-xs font-semibold text-gray-800">{t.sourceFarm}</p>
                        <p className="text-xs text-gray-400">{t.sourceStore}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs font-semibold text-gray-800">{t.destinationFarm}</p>
                        <p className="text-xs text-gray-400">{t.destinationStore}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{t.eggGrade}</td>
                      <td className="px-4 py-3">
                        <p className="text-xs font-bold text-gray-900">{t.quantity.toLocaleString()}</p>
                        <p className="text-xs text-gray-400">{t.trays} trays{t.looseEggs > 0 ? ` + ${t.looseEggs}` : ''}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{t.transferredBy}</td>
                      <td className="px-4 py-3">
                        {t.receivedBy ? (
                          <span className="text-xs text-gray-700 font-medium">{t.receivedBy}</span>
                        ) : t.status === 'in_transit' ? (
                          <button
                            onClick={() => setReceiveTransfer(t)}
                            className="text-xs font-semibold text-green-600 hover:text-green-700 flex items-center gap-1 whitespace-nowrap"
                          >
                            <CheckCircle size={11} /> Confirm Receipt
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400 italic">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setViewTransfer(t)}
                            title="View"
                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <Eye size={13} />
                          </button>
                          {['draft', 'pending'].includes(t.status) && (
                            <button
                              onClick={() => setEditTransfer(t)}
                              title="Edit"
                              className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors"
                            >
                              <Edit3 size={13} />
                            </button>
                          )}
                          {t.status === 'pending' && (
                            <button
                              onClick={() => handleStatusChange(t.id, 'in_transit')}
                              title="Mark In Transit"
                              className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors"
                            >
                              <Truck size={13} />
                            </button>
                          )}
                          {t.status === 'in_transit' && (
                            <button
                              onClick={() => setReceiveTransfer(t)}
                              title="Confirm Receipt"
                              className="p-1.5 rounded-lg hover:bg-green-50 text-gray-400 hover:text-green-600 transition-colors"
                            >
                              <CheckCircle size={13} />
                            </button>
                          )}
                          {['draft', 'pending'].includes(t.status) && (
                            <button
                              onClick={() => handleStatusChange(t.id, 'cancelled')}
                              title="Cancel"
                              className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <XCircle size={13} />
                            </button>
                          )}
                          {['draft', 'cancelled'].includes(t.status) && (
                            <button
                              onClick={() => handleDelete(t.id)}
                              title="Delete"
                              className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                <p className="text-xs text-gray-400">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-7 h-7 rounded-lg text-xs font-semibold transition-colors ${
                        p === page ? 'bg-green-600 text-white' : 'text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRightIcon size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Workflow Legend */}
      <div className="mt-4 bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <p className="text-xs font-bold text-gray-600 mb-3 uppercase tracking-wide">Transfer Status Workflow</p>
        <div className="flex flex-wrap items-center gap-4">
          {(Object.entries(STATUS_CONFIG) as [EggTransfer['status'], typeof STATUS_CONFIG[EggTransfer['status']]][]).map(([key, cfg]) => {
            const Icon = cfg.icon;
            return (
              <div key={key} className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color}`}>
                  <Icon size={11} />{cfg.label}
                </span>
                {key !== 'cancelled' && key !== 'received' && (
                  <ChevronRightIcon size={12} className="text-gray-300" />
                )}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Transfers progress: Draft → Pending → In Transit → Received. Cancelled transfers are terminal.
        </p>
      </div>
    </div>
  );
}
